import axios from "axios"
import Swal from "sweetalert2";
import { AuthService } from "./auth.service";
import { LoginModel } from "../models/login.model";
import { ListModel } from "../models/list.model";
import { UserModel } from "../models/user.model";
import { AuthModel } from "../models/auth.model";
import { ActivityModel } from "../models/activity.model";
import { getCurrentDate } from "../lib/utils";
import { SensorModel } from "../models/sensor.model";
import { v4 as uuidv4 } from 'uuid';

// activity_report_details
// deployment_id

const customSwalStyling = {
    popup: "rounded-lg bg-white shadow-lg p-6 dark:bg-gray-800",
    title: "text-lg font-semibold text-gray-900 dark:text-gray-100",
    htmlContainer: "text-gray-600 dark:text-gray-300",
    confirmButton: "bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded",
    cancelButton: "bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded",
}

const client = axios.create({
    baseURL: process.env.REACT_APP_API_BASE,
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'mode': 'cors'
    },
    validateStatus() {
        return true
    }
})

export class DataService {
    static async login(username: string, password: string) {
        if (username === '' && password === '') return false

        try {
            Swal.fire({
                title: "Please wait",
                text: "We are processing your request!",
                customClass: customSwalStyling,
                didOpen: () => {
                    Swal.showLoading()
                }
            })

            const rsp = await client.request<LoginModel>({
                url: '/function/well-api/api',
                method: 'POST',
                data: {
                    "function": "credentials",
                    "clientId": "001",
                    "user_name": username,
                    "ps": password,
                    "nonce": uuidv4()
                }
            })

            if (rsp.data.status === "200 OK" || rsp.status === 200) {
                Swal.close()
                AuthService.saveAuth({
                    username: username,
                    token: rsp.data.access_token,
                    user_id: rsp.data.user_id
                })
                return true
            }

            // Bad credentials
            Swal.fire({
                icon: "error",
                title: "Login Failed",
                text: "Invalid username or password!",
                customClass: customSwalStyling
            })
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: "Server Unreachable",
                text: "Something went wrong while contacting our servers, please try again later!",
                customClass: customSwalStyling
            })
        }

        return false
    }

    static async getUsers(silent: boolean, forDate: string = getCurrentDate()): Promise<ListModel<UserModel>> {
        if (!silent) {
            Swal.fire({
                title: "Please wait",
                text: "Beneficiaries are getting processed!",
                customClass: customSwalStyling,
                didOpen: () => {
                    Swal.showLoading()
                }
            })
        }

        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ListModel<UserModel>>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "dashboard_list",
                "user_name": auth.username,
                "token": auth.token,
                "date": forDate,
                "nonce": uuidv4()
            }
        })


        if (rsp.data.status !== "200 OK" || rsp.status !== 200) {
            this.throwSessionExpired()
        }

        Swal.close()
        return rsp.data
    }

    static async getUserById(id: number, silent: boolean, forDate: string = getCurrentDate()): Promise<UserModel> {
        if (!silent) {
            Swal.fire({
                title: "Please wait",
                text: "Latest details are getting loaded!",
                customClass: customSwalStyling,
                didOpen: () => {
                    Swal.showLoading()
                }
            })
        }

        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ListModel<UserModel>>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "dashboard_single",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "date": forDate,
                "nonce": uuidv4()
            }
        })

        if (rsp.data.status !== "200 OK" || rsp.status !== 200) {
            this.throwSessionExpired()
        }

        Swal.close()
        return rsp.data.result_list[0]
    }

    static async getAcitivityReport(id: number) {
        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<ActivityModel>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "activities_report_details",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "nonce": uuidv4()
            }
        })
        return rsp.data
    }

    static async getSensorData(id: number, sensor: string, room: string, forDate: string = getCurrentDate()) {
        //["temperature", "humidity", "pressure", "light", "radar", "voc", "co2,]
        const auth: AuthModel = AuthService.getAuth()
        const rsp = await client.request<SensorModel>({
            url: '/function/well-api/api',
            method: 'POST',
            data: {
                "function": "get_sensor_bucketed_data_by_room_sensor",
                "user_name": auth.username,
                "token": auth.token,
                "deployment_id": id,
                "sensor": sensor,
                'data_type': 'ML',
                'radar_part': 's28',
                "date": forDate,
                "bucket_size": '5m', //(10s, 1m, 5m, 10m, 15m, 30m, 1h)
                location: room,
                "nonce": uuidv4()
            }
        })

        if (rsp.data.status !== "200 OK" || rsp.status !== 200)
            throw new Error(`Failed to load details for ${sensor}`)
        return rsp.data
    }

    private static throwSessionExpired() {
        AuthService.clearAuth()
        Swal.fire({
            icon: "error",
            title: "Session Expired",
            text: "Your session has expired! Please log in again to continue.",
            customClass: customSwalStyling
        })
        throw new Error('UNAUTHORIZED')
    }
}